import Urls from "./Urls";
import {
  Announcement,
  Intermission,
  IntermissionInput,
  SocialLink,
  CtaCard,
  CommunityStats,
  GalleryResponse,
  GalleryBandsPage,
  GalleryDrawing,
  LeaderboardResponse,
  EloHighlight,
  EloEntry,
  PcaData,
  PopularCategory,
  QuestionInfo,
  AnalyticsInsight,
  AnalyticsDeckEntry,
} from "../interfaces/GameLogicTypes";

const fetchJson = async (url: string): Promise<any> => {
  const resp = await fetch(url, { credentials: "include" });
  if (!resp.ok) return null;
  return resp.json();
};

export default class CommunityApi {
  static async getAnnouncements(): Promise<Announcement[]> {
    return (await fetchJson(Urls.ANNOUNCEMENT)) ?? [];
  }

  static async getStats(): Promise<CommunityStats | null> {
    return fetchJson(Urls.COMMUNITY_STATS);
  }

  static async getGalleryBands(langs: string[], seed: number, offset: number, limit: number): Promise<GalleryBandsPage | null> {
    const q = new URLSearchParams({ langs: langs.join(","), seed: String(seed), offset: String(offset), limit: String(limit) });
    return fetchJson(`${Urls.COMMUNITY_GALLERY_BANDS}?${q}`);
  }

  static async getGalleryDrawings(ids: number[], visitorId: string): Promise<GalleryDrawing[] | null> {
    const q = new URLSearchParams({ ids: ids.join(","), visitorId });
    return fetchJson(`${Urls.COMMUNITY_GALLERY_DRAWINGS}?${q}`);
  }

  static async getGallery(limit: number = 20, offset: number = 0, visitorId?: string): Promise<GalleryResponse | null> {
    let url = `${Urls.COMMUNITY_GALLERY}?limit=${limit}&offset=${offset}`;
    if (visitorId) url += `&visitorId=${encodeURIComponent(visitorId)}`;
    return fetchJson(url);
  }

  static async voteGallery(
    submissionId: number,
    visitorId: string,
    action: string,
    token?: string
  ): Promise<{ likeCount: number; dislikeCount: number; reportCount: number; myVotes: string[] } | null> {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const resp = await fetch(`${Urls.COMMUNITY_GALLERY}/${submissionId}/vote`, {
      method: "POST",
      headers,
      body: JSON.stringify({ visitorId, action }),
    });
    if (!resp.ok) return null;
    return resp.json();
  }

  static async getTopCreators(): Promise<LeaderboardResponse | null> {
    return fetchJson(Urls.COMMUNITY_TOP_CREATORS);
  }

  static async getEloHighlights(): Promise<EloHighlight[] | null> {
    return fetchJson(Urls.COMMUNITY_ELO_HIGHLIGHTS);
  }

  static async getQuestions(deckId: string, categoryName: string): Promise<QuestionInfo[]> {
    return (await fetchJson(
      `${Urls.COMMUNITY_QUESTIONS}/${encodeURIComponent(deckId)}/${encodeURIComponent(categoryName)}`
    )) ?? [];
  }

  static async getAnalytics(deckId: string, categoryName: string, questionCardId: number): Promise<EloEntry[] | null> {
    return fetchJson(`${Urls.COMMUNITY_ANALYTICS}/${encodeURIComponent(deckId)}/${encodeURIComponent(categoryName)}/${questionCardId}`);
  }

  static async getPca(deckId: string, categoryName: string, questionCardId: number): Promise<PcaData | null> {
    return fetchJson(`${Urls.COMMUNITY_PCA}/${encodeURIComponent(deckId)}/${encodeURIComponent(categoryName)}/${questionCardId}`);
  }

  static async getPopularCategories(deckId?: string): Promise<PopularCategory[]> {
    const url = deckId
      ? `${Urls.COMMUNITY_POPULAR_CATEGORIES}?deckId=${encodeURIComponent(deckId)}`
      : Urls.COMMUNITY_POPULAR_CATEGORIES;
    return (await fetchJson(url)) ?? [];
  }

  static async getAnalyticsInsights(): Promise<AnalyticsInsight[]> {
    return (await fetchJson(Urls.COMMUNITY_ANALYTICS_INSIGHTS)) ?? [];
  }

  static async getAnalyticsDecks(limit?: number, search?: string): Promise<AnalyticsDeckEntry[]> {
    const params = new URLSearchParams();
    if (limit) params.set("limit", String(limit));
    if (search) params.set("search", search);
    const qs = params.toString();
    return (await fetchJson(qs ? `${Urls.COMMUNITY_ANALYTICS_DECKS}?${qs}` : Urls.COMMUNITY_ANALYTICS_DECKS)) ?? [];
  }

  // Admin methods
  static async getAdminAnnouncements(secret: string): Promise<Announcement[]> {
    const resp = await fetch(Urls.ADMIN_ANNOUNCEMENTS, {
      headers: { "x-admin-secret": secret },
    });
    if (!resp.ok) return [];
    return resp.json();
  }

  static async createAnnouncement(secret: string, title: string, body: string, priority: number = 0): Promise<Announcement | null> {
    const resp = await fetch(Urls.ADMIN_ANNOUNCEMENTS, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-admin-secret": secret },
      body: JSON.stringify({ title, body, priority }),
    });
    if (!resp.ok) return null;
    return resp.json();
  }

  static async updateAnnouncement(secret: string, id: number, data: Partial<{ title: string; body: string; active: boolean; priority: number }>): Promise<Announcement | null> {
    const resp = await fetch(`${Urls.ADMIN_ANNOUNCEMENTS}/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "x-admin-secret": secret },
      body: JSON.stringify(data),
    });
    if (!resp.ok) return null;
    return resp.json();
  }

  static async deleteAnnouncement(secret: string, id: number): Promise<boolean> {
    const resp = await fetch(`${Urls.ADMIN_ANNOUNCEMENTS}/${id}`, {
      method: "DELETE",
      headers: { "x-admin-secret": secret },
    });
    return resp.ok;
  }

  // Intermissions — admin messages broadcast into running games.
  // The image travels as a base64 data URL inside the JSON body; the backend gives these routes
  // their own larger body limit, and never returns the bytes back (only hasImage).
  static async getIntermissions(secret: string): Promise<Intermission[]> {
    const resp = await fetch(Urls.ADMIN_INTERMISSIONS, {
      headers: { "x-admin-secret": secret },
    });
    if (!resp.ok) return [];
    return resp.json();
  }

  static async createIntermission(secret: string, data: IntermissionInput): Promise<Intermission | null> {
    const resp = await fetch(Urls.ADMIN_INTERMISSIONS, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-admin-secret": secret },
      body: JSON.stringify(data),
    });
    if (!resp.ok) return null;
    return resp.json();
  }

  static async updateIntermission(secret: string, id: number, data: Partial<IntermissionInput>): Promise<Intermission | null> {
    const resp = await fetch(`${Urls.ADMIN_INTERMISSIONS}/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "x-admin-secret": secret },
      body: JSON.stringify(data),
    });
    if (!resp.ok) return null;
    return resp.json();
  }

  static async deleteIntermission(secret: string, id: number): Promise<boolean> {
    const resp = await fetch(`${Urls.ADMIN_INTERMISSIONS}/${id}`, {
      method: "DELETE",
      headers: { "x-admin-secret": secret },
    });
    return resp.ok;
  }

  // CTA cards
  static async getCtaCards(): Promise<CtaCard[]> {
    return (await fetchJson(Urls.CTA_CARDS)) ?? [];
  }

  static async getAdminCtaCards(secret: string): Promise<CtaCard[]> {
    const resp = await fetch(Urls.ADMIN_CTA_CARDS, { headers: { "x-admin-secret": secret } });
    if (!resp.ok) return [];
    return resp.json();
  }

  static async createCtaCard(secret: string, data: { icon: string; title: string; body: string; link: string; enabled?: boolean; sortOrder?: number }): Promise<CtaCard | null> {
    const resp = await fetch(Urls.ADMIN_CTA_CARDS, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-admin-secret": secret },
      body: JSON.stringify(data),
    });
    if (!resp.ok) return null;
    return resp.json();
  }

  static async updateCtaCard(secret: string, id: number, data: Partial<{ icon: string; title: string; body: string; link: string; enabled: boolean; sortOrder: number }>): Promise<CtaCard | null> {
    const resp = await fetch(`${Urls.ADMIN_CTA_CARDS}/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "x-admin-secret": secret },
      body: JSON.stringify(data),
    });
    if (!resp.ok) return null;
    return resp.json();
  }

  static async deleteCtaCard(secret: string, id: number): Promise<boolean> {
    const resp = await fetch(`${Urls.ADMIN_CTA_CARDS}/${id}`, {
      method: "DELETE",
      headers: { "x-admin-secret": secret },
    });
    return resp.ok;
  }

  // Social Links
  static async getSocialLinks(): Promise<SocialLink[]> {
    const resp = await fetch(Urls.SOCIAL_LINKS);
    if (!resp.ok) return [];
    return resp.json();
  }

  static async getAdminSocialLinks(secret: string): Promise<SocialLink[]> {
    const resp = await fetch(Urls.ADMIN_SOCIAL_LINKS, { headers: { "x-admin-secret": secret } });
    if (!resp.ok) return [];
    return resp.json();
  }

  static async createSocialLink(secret: string, data: { label: string; url: string; svgMarkup: string; enabled?: boolean; sortOrder?: number }): Promise<SocialLink | null> {
    const resp = await fetch(Urls.ADMIN_SOCIAL_LINKS, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-admin-secret": secret },
      body: JSON.stringify(data),
    });
    if (!resp.ok) return null;
    return resp.json();
  }

  static async updateSocialLink(secret: string, id: number, data: Partial<{ label: string; url: string; svgMarkup: string; enabled: boolean; sortOrder: number }>): Promise<SocialLink | null> {
    const resp = await fetch(`${Urls.ADMIN_SOCIAL_LINKS}/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "x-admin-secret": secret },
      body: JSON.stringify(data),
    });
    if (!resp.ok) return null;
    return resp.json();
  }

  static async deleteSocialLink(secret: string, id: number): Promise<boolean> {
    const resp = await fetch(`${Urls.ADMIN_SOCIAL_LINKS}/${id}`, {
      method: "DELETE",
      headers: { "x-admin-secret": secret },
    });
    return resp.ok;
  }

  // Picasso labeler
  static async getPicassoLabelerNext(secret: string, opts?: { exclude?: string; language?: string; skippedIds?: number[] }): Promise<any> {
    const resp = await fetch(`${Urls.ADMIN_PICASSO_LABELER}/next`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-admin-secret": secret },
      body: JSON.stringify({
        exclude: opts?.exclude,
        language: opts?.language,
        skippedIds: opts?.skippedIds,
      }),
    });
    if (!resp.ok) return null;
    return resp.json();
  }

  static async markPicassoSuitable(secret: string, questionId: number): Promise<boolean> {
    const resp = await fetch(`${Urls.ADMIN_PICASSO_LABELER}/${questionId}`, {
      method: "PATCH",
      headers: { "x-admin-secret": secret },
    });
    return resp.ok;
  }

  static async unmarkPicassoSuitable(secret: string, questionId: number): Promise<boolean> {
    const resp = await fetch(`${Urls.ADMIN_PICASSO_LABELER}/${questionId}`, {
      method: "DELETE",
      headers: { "x-admin-secret": secret },
    });
    return resp.ok;
  }
}
